<script setup lang="ts">
import { useExtractedDatesStore } from '@/store/extractedDatesStore';
import { useUserStore } from '@/store/userStore';
import { auth } from '@/common/helpers/firebase';
import { ExtractedDate } from '@/types/extractedDate';
import { format } from 'date-fns';
import { debounce } from 'lodash';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import ExtractedDateItem from './ExtractedDateItem.vue';
import RegistrationModal from './RegistrationModal.vue';

const nitteHost = import.meta.env.VITE_APP_TEXT_GEN_HOST
const extractedDatesStore = useExtractedDatesStore()
const { extractedDates, isExtracting, extractionError, hasExtractedDates, isLoadingEvents } = storeToRefs(extractedDatesStore)

type DateGroup = { dateLabel: string; items: ExtractedDate[] }

const groupedDates = computed((): DateGroup[] => {
  const groups: DateGroup[] = []
  for (const d of extractedDates.value) {
    const dateLabel = d.start ? format(d.start, 'M月d日(eee)').replace(/Mon|Tue|Wed|Thu|Fri|Sat|Sun/, (m) => ({ Mon: '月', Tue: '火', Wed: '水', Thu: '木', Fri: '金', Sat: '土', Sun: '日' })[m] ?? m) : ''
    const last = groups[groups.length - 1]
    if (last && last.dateLabel === dateLabel) {
      last.items.push(d)
    } else {
      groups.push({ dateLabel, items: [d] })
    }
  }
  return groups
})
const userStore = useUserStore()
const { isLoggedIn, isCalendarScopeAuthorized } = storeToRefs(userStore)

const showCalendarLink = computed(() => !isLoggedIn.value || !isCalendarScopeAuthorized.value)

const isTouchDevice = window.matchMedia('(pointer: coarse)').matches
const showLinkedTooltip = ref(false)
const toggleLinkedTooltip = () => {
  if (!isTouchDevice) return
  showLinkedTooltip.value = !showLinkedTooltip.value
}

const SAMPLE_USED_KEY = 'nitte-text-receive-sample-used'
const showSampleButton = ref(!localStorage.getItem(SAMPLE_USED_KEY) && !extractedDatesStore.inputText)

const fillSample = () => {
  extractedDatesStore.inputText = 'お疲れ様です！来週の月曜の午後2時以降か、木曜の午前中でどうでしょう？'
  showSampleButton.value = false
  localStorage.setItem(SAMPLE_USED_KEY, '1')
}

const debouncedExtract = debounce(async () => {
  await extractedDatesStore.extractDates()
  if (isCalendarScopeAuthorized.value && extractedDatesStore.hasExtractedDates) {
    extractedDatesStore.fetchEventsForAllCandidates()
  }
}, 500)

watch(() => extractedDatesStore.inputText, (newVal) => {
  if (extractedDatesStore.skipNextExtraction) {
    extractedDatesStore.skipNextExtraction = false
    return
  }
  if (!newVal.trim()) {
    extractedDatesStore.reset()
    return
  }
  debouncedExtract()
})

const emit = defineEmits<{
  (e: 'open-how-to-use'): void
  (e: 'open-settings'): void
}>()
</script>

<template>
  <section class="input-panel lo-v fill-wt bg-white">
    <div class="textarea-wrapper mg-top-8px">
      <textarea
        class="form-base input-textarea"
        v-model="extractedDatesStore.inputText"
        :rows="8"
        placeholder="お疲れ様です。面談の件ですが、来週4月7日(火)の午後2時か、4月9日(木)の午前中でご都合いかがでしょうか。"
      />
      <button v-if="showSampleButton" class="sample-button" @click="fillSample">サンプルを入力</button>
    </div>
    <div class="action-row">
      <button class="action-btn" @click="emit('open-how-to-use')" title="使い方">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
        使い方
      </button>
      <button class="action-btn" @click="emit('open-settings')" title="カレンダー設定">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
        設定
      </button>
    </div>

    <div v-if="isExtracting" class="mg-top-12px extracting">
      <div class="spinner"></div>
      <sup-label class="gray-1">候補日時を抽出中...</sup-label>
    </div>
    <div v-else-if="isLoadingEvents" class="mg-top-12px extracting">
      <div class="spinner"></div>
      <sup-label class="gray-1">予定を取得しています...</sup-label>
    </div>

    <div v-if="extractionError" class="mg-top-8px error-message">
      {{ extractionError }}
    </div>

    <div v-if="hasExtractedDates" class="mg-top-16px">
      <div class="extracted-header">
        <sup-label class="gray-1"></sup-label>
        <div v-if="!showCalendarLink" class="calendar-linked-wrapper" v-click-outside="() => showLinkedTooltip = false">
          <div class="calendar-linked" @click="toggleLinkedTooltip">
            <img v-if="userStore.userIcon" :src="userStore.userIcon" class="calendar-linked-icon" />
            <sup-label class="calendar-linked-label">連携中</sup-label>
          </div>
          <div class="calendar-linked-tooltip" :class="{ 'tooltip-active': showLinkedTooltip }">
            <div class="calendar-linked-tooltip-inner">
              <a href="https://nitte.app/settings/account" target="_blank">アカウント設定</a>> 連携中のアカウントで選択しているカレンダーの予定が反映されます。
              <span class="tooltip-logout" @click="auth.signOut()">ログアウト</span>
            </div>
          </div>
        </div>
        <RegistrationModal v-else>
          <button class="calendar-link-button">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M5 1v2M11 1v2M1 6h14M3 3h10a2 2 0 012 2v8a2 2 0 01-2 2H3a2 2 0 01-2-2V5a2 2 0 012-2z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
            カレンダー連携
          </button>
        </RegistrationModal>
      </div>
      <div class="extracted-list mg-top-8px">
        <div v-for="group in groupedDates" :key="group.dateLabel" class="date-group">
          <div class="date-group-header">{{ group.dateLabel }}</div>
          <div class="date-group-items">
            <extracted-date-item
              v-for="date in group.items"
              :key="date.id"
              :extracted-date="date"
            />
          </div>
        </div>
      </div>
    </div>

  </section>
</template>

<style scoped>
.input-panel {
  padding: 24px 36px 36px;
  width: 100%;
}
@media (max-width: 800px) {
  .input-panel {
    padding: 20px 16px 28px;
  }
}
.input-textarea {
  resize: vertical;
  min-height: 180px;
}
.extracted-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.date-group-header {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--font-gray-2);
  padding: 0 4px 4px;
}
.date-group-items {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.extracted-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.calendar-link-button {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: var(--main);
  border: none;
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 5px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: opacity 0.15s ease;
}
.calendar-link-button:hover {
  opacity: 0.85;
}
.calendar-linked-wrapper {
  position: relative;
}
.calendar-linked-wrapper:hover .calendar-linked-tooltip,
.calendar-linked-tooltip.tooltip-active {
  display: block;
}
.calendar-linked {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.calendar-linked-tooltip {
  display: none;
  position: absolute;
  bottom: calc(100% - 4px);
  right: 0;
  padding-bottom: 8px;
  font-size: 0.75rem;
  color: var(--font-gray-1);
  line-height: 1.5;
  white-space: nowrap;
  z-index: 200;
}
@media (max-width: 800px) {
  .calendar-linked-wrapper {
    position: static;
  }
  .extracted-header {
    position: relative;
  }
  .calendar-linked-tooltip {
    right: 0;
    left: 0;
    white-space: normal;
  }
}
.calendar-linked-tooltip-inner {
  background: white;
  border: 0.5px solid #d1d5db;
  border-radius: 8px;
  padding: 8px 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.10);
}
.calendar-linked-tooltip a {
  color: var(--link);
}
.tooltip-logout {
  margin-left: 8px;
  color: var(--font-gray-3);
  cursor: pointer;
  text-decoration: underline;
}
.tooltip-logout:hover {
  color: var(--error);
}
.calendar-linked-icon {
  width: 20px;
  height: 20px;
  border-radius: 50%;
}
.calendar-linked-label {
  color: var(--main);
  font-weight: 600;
}
.error-message {
  color: var(--error);
  font-size: var(--font-size-14px);
}
.extracting {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}
.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid var(--border-gray);
  border-top-color: var(--main);
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  flex-shrink: 0;
  margin-top: 1px;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
.textarea-wrapper {
  position: relative;
}
.textarea-wrapper .input-textarea {
  margin-top: 0;
}
.sample-button {
  position: absolute;
  bottom: 14px;
  right: 8px;
  background: white;
  border: 1px solid #d1d5db;
  color: var(--font-gray-2);
  font-size: 0.75rem;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
}
.sample-button:hover {
  border-color: var(--main);
  color: var(--main);
}
.action-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 4px;
}
.action-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: none;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--font-gray-2);
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
  white-space: nowrap;
}
.action-btn:hover {
  color: var(--main);
  border-color: var(--main);
}
</style>
